// https://www.radix-ui.com/primitives/docs/components/dialog
import * as DialogPrimitive from "@radix-ui/react-dialog";
import type { ReactNode } from "react";
import cx from "ui/cx";
import IconClose from "ui/icons/IconClose";
import styles from "./Drawer.module.scss";

export interface DrawerProps {
  /** Whether the drawer is open. */
  open: boolean;
  /** Fired when the drawer requests to close (close button, Esc, or backdrop click). */
  onOpenChange: (open: boolean) => void;
  /** Title shown in the drawer header, beside the close button. */
  title?: ReactNode;
  /** Drawer body content. */
  children?: ReactNode;
  /** Optional slot for extra header actions, rendered between the title and close button. */
  titleActions?: ReactNode;
  /** One-off override for the content panel. */
  className?: string;
}

/**
 * A panel that slides in from the right over a dimmed backdrop. Built on Radix Dialog,
 * so it handles focus trapping, Esc-to-close, and accessibility out of the box.
 *
 * Intentionally product-agnostic — pass a `title` and arbitrary `children`.
 */
export default function Drawer({ open, onOpenChange, title, children, titleActions, className }: DrawerProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={styles.overlay} data-testid="drawer-overlay" />
        <DialogPrimitive.Content
          className={cx(styles.content, className)}
          data-testid="drawer-content"
          // No descriptive body text; opt out of Radix's aria-describedby warning.
          aria-describedby={undefined}
        >
          <div className={styles.header}>
            <DialogPrimitive.Title className={styles.title}>{title}</DialogPrimitive.Title>
            {titleActions}
            <DialogPrimitive.Close className={styles.close} aria-label="Close" data-testid="drawer-close">
              <IconClose size={18} />
            </DialogPrimitive.Close>
          </div>
          <div className={styles.body}>{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
